import { DataResponse, Dimension, Measure } from '@embeddable.com/core';
import { Theme } from '../../../../theme/theme.types';
import { ChartData, ChartOptions } from 'chart.js';
import { getThemeFormatter } from '../../../../theme/formatter/formatter.utils';
import { mergician } from 'mergician';
import { getDimensionMeasureColor } from '../../../../theme/styles/styles.utils';
import { setColorAlpha } from '../../../../utils/color.utils';
import { getChartColors } from '@embeddable.com/remarkable-ui';
import { getDimensionWithoutTruncation } from '../../charts.utils';

export const getLineChartGroupedProData = (
  props: {
    data: DataResponse['data'];
    dimension: Dimension;
    groupDimension: Dimension;
    measure: Measure;
    hasMinMaxYAxisRange: boolean;
  },
  theme: Theme,
): ChartData<'line'> => {
  const themeFormatter = getThemeFormatter(theme);
  const { data = [], dimension, groupDimension, measure, hasMinMaxYAxisRange } = props;

  const axis = [...new Set(data.map((d) => d[dimension.name]).filter((d) => d != null))].sort();
  const groupBy = [...new Set(data.map((d) => d[groupDimension.name]))].filter((d) => d != null);

  const chartColors = getChartColors();
  const datasets: ChartData<'line'>['datasets'] = groupBy.map((groupByItem, index) => {
    const backgroundColor = getDimensionMeasureColor({
      dimensionOrMeasure: groupDimension,
      theme,
      color: 'background',
      value: `${groupDimension.name}.${groupByItem}`,
      chartColors,
      index,
    });

    const borderColor = getDimensionMeasureColor({
      dimensionOrMeasure: groupDimension,
      theme,
      color: 'border',
      value: `${groupDimension.name}.${groupByItem}`,
      chartColors,
      index,
    });

    const dataset = {
      clip: hasMinMaxYAxisRange,
      label: themeFormatter.data(groupDimension, groupByItem),
      rawLabel: groupByItem,
      backgroundColor: setColorAlpha(backgroundColor, 0.5),
      pointBackgroundColor: backgroundColor,
      fill: measure.inputs?.['fillUnderLine'],
      borderColor,
      data: axis.map((axisItem) => {
        const record = data.find(
          (d) => d[groupDimension.name] === groupByItem && d[dimension.name] === axisItem,
        );
        return record?.[measure.name] ?? (measure.inputs?.['connectGaps'] ? 0 : null);
      }),
    } as ChartData<'line'>['datasets'][number];

    return dataset;
  });

  return {
    labels: axis,
    datasets,
  };
};

export const getLineChartGroupedProOptions = (
  options: {
    dimension: Dimension;
    groupDimension: Dimension;
    measure: Measure;
    data: ChartData<'line'>;
  },
  theme: Theme,
): ChartOptions<'line'> => {
  const { dimension, groupDimension, data, measure } = options;
  const themeFormatter = getThemeFormatter(theme);

  const lineChartOptions: ChartOptions<'line'> = {
    plugins: {
      datalabels: {
        labels: {
          value: {
            formatter: (value: string | number) => {
              return themeFormatter.data(measure, value);
            },
          },
        },
      },
      tooltip: {
        callbacks: {
          title: (context) => {
            const label = context[0]?.label;
            return themeFormatter.data(getDimensionWithoutTruncation(dimension), label);
          },
          label: (context) => {
            const raw = context.raw as number;

            const label = themeFormatter.data(
              getDimensionWithoutTruncation(groupDimension),
              (context.dataset as { rawLabel?: string }).rawLabel,
            );
            return `${label}: ${themeFormatter.data(measure, raw)}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          callback: (value) => {
            if (!data || !data.labels) return undefined;

            const label = data.labels[Number(value)] as string;
            return themeFormatter.data(dimension, label);
          },
        },
      },
      y: {
        ticks: {
          callback: (value) => {
            return themeFormatter.data(measure, value);
          },
        },
      },
    },
  };

  return mergician(lineChartOptions, theme.charts?.lineChartGroupedPro?.options || {});
};
